import { useEffect, useState, useRef, useCallback, lazy, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLayoutVisibility } from '@context/LayoutContext'
import { useLanguage } from '@context/LanguageContext'
import { usePuzzle } from '../../context/PuzzleContext'
import { usePuzzleEngine } from '../../hooks/usePuzzleEngine'
import { handlePlaySound } from '@handlers/handlePlaySound'
import { PUZZLE_IMAGE_PATHS, PUZZLE_TIMINGS } from '../../constants/puzzle'
import { ExitGameButton } from '@shared'
import BoardStage from '../ui/BoardStage'
import PuzzleBoard from '../ui/PuzzleBoard'
import styles from './PuzzleGame.module.css'

const setIfChanged = (setter, next) => setter(prev => (Object.is(prev, next) ? prev : next))
const ExitPuzzleModal = lazy(() => import('../ui/ExitPuzzleModal'))

export default function PuzzleGame ({
  difficulty = 'easy',
  onExit,
  onSolved
}) {
  useLayoutVisibility({ header: false, footer: false })
  const { t, lang } = useLanguage()
  const { gameSnapshot } = usePuzzle()
  const navigate = useNavigate()
  const imageSrc = gameSnapshot?.imageSrc ?? PUZZLE_IMAGE_PATHS.cover

  // UI state for blocking input
  const [inputBlocked, setInputBlocked] = useState(false)

  // Edge-trigger refs to avoid re-renders
  const prevSolvedRef = useRef(false)

  // One-shot guard to avoid re-triggering victory side effects
  const doneRef = useRef(false)

  // Timers registry to cleanup on unmount or on reshuffle/start
  const victoryTimersRef = useRef(new Set())

  const {
    board,
    size,
    isSolved,
    isStarted,
    start,
    reshuffle,
    moveTile,
    canMoveTile
  } = usePuzzleEngine()

  // START: reset state and start new game
  useEffect(() => {
    // Clean up previous state
    victoryTimersRef.current.forEach(clearTimeout)
    victoryTimersRef.current.clear()
    doneRef.current = false
    setIfChanged(setInputBlocked, false)

    start({ difficulty })
  }, [difficulty, start])

  // VICTORY: edge-trigger only when resolved first time
  useEffect(() => {
    const becameSolved = !prevSolvedRef.current && isSolved
    prevSolvedRef.current = isSolved

    if (!becameSolved || !isStarted || doneRef.current) return

    doneRef.current = true
    setIfChanged(setInputBlocked, true)

    // Schedule victory sound (respects soundEnabled via soundManager)
    const endSoundTimer = setTimeout(() => {
      handlePlaySound('end')
    }, PUZZLE_TIMINGS.victorySound)

    // Notify parent
    const endGameTimer = setTimeout(() => {
      if (typeof onSolved === 'function') onSolved()
    }, PUZZLE_TIMINGS.endScreenTransition)

    victoryTimersRef.current.add(endSoundTimer)
    victoryTimersRef.current.add(endGameTimer)
  }, [isSolved, isStarted, onSolved])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      victoryTimersRef.current.forEach(clearTimeout)
      victoryTimersRef.current.clear()
    }
  }, [])

  // Stable handlers
  const [showExitModal, setShowExitModal] = useState(false)

  const handleExit = useCallback(() => {
    setShowExitModal(true)
  }, [])

  const handleConfirmExit = useCallback(() => {
    if (typeof onExit === 'function') onExit()
    setShowExitModal(false)
    navigate(`/${lang}`)
  }, [onExit, navigate, lang])

  const handleReshuffle = useCallback(() => {
    // Reset local state before reshuffling
    victoryTimersRef.current.forEach(clearTimeout)
    victoryTimersRef.current.clear()
    doneRef.current = false
    prevSolvedRef.current = false
    setIfChanged(setInputBlocked, false)

    reshuffle({ difficulty })
  }, [difficulty, reshuffle])

  // GiveUp temporarily shows exit modal (future presents solution and CTA to restart/home)
  const handleGiveUp = useCallback(() => {
    setShowExitModal(true)
  }, [])

  const handleTileClick = useCallback((index) => {
    moveTile(index)
  }, [moveTile])

  return (
    <section
      className={styles.puzzleGame}
      role='region'
      aria-label={t.shared.access.game.area}
    >
      {/* Exit button top right */}
      <ExitGameButton onClick={handleExit} />

      {/* Top bar: title */}
      <div className={styles.topbar}>
        <h1 className={styles.title}>{t.puzzle.title}</h1>
      </div>

      {/* Board stage: positions and sizes the board consistently */}
      <BoardStage size={size}>
        <PuzzleBoard
          board={board}
          size={size}
          imageSrc={imageSrc}
          interactive
          inputBlocked={inputBlocked}
          canMoveTile={canMoveTile}
          onTileClick={handleTileClick}
        />
      </BoardStage>

      {/* Controls */}
      <div className={styles.controls}>
        <button
          type='button'
          className={styles.controlsBtn}
          onClick={handleReshuffle}
          aria-label={t.puzzle.access.reshuffle ?? t.shared.access.game.restart}
        >
          {t.puzzle.game.reshuffle}
        </button>
        <button
          type='button'
          className={styles.controlsBtn}
          onClick={handleGiveUp}
          aria-label={t.puzzle.access.giveUp ?? t.shared.access.game.reset}
        >
          {t.puzzle.game.giveUp}
        </button>
      </div>

      {/* Exit Modal */}
      {showExitModal && (
        <Suspense fallback={null}>
          <ExitPuzzleModal
            onClose={() => setShowExitModal(false)}
            onExit={() => handleConfirmExit()}
            imageSrc={imageSrc}
          />
        </Suspense>
      )}
    </section>
  )
}
